import { TicketFilterRequest } from "callable-data-objects";
import { DBReference } from "callable-data-objects/dist/models/accountDevice/AccountDevice";
import AccountSelectWithContext from "Components/Shared/AccountSelectWithContext";
import MenuSearch from "Components/Shared/MenuSearch";
import PortStatusSubmissionStatusSelect from "Components/Shared/PortSubmissionStatusSelect";
import TicketStatusSelect from "Components/Shared/TicketStatusSelect";
import TicketTypeSelect from "Components/Shared/TicketTypeSelect";
import useCheckRole from "Hooks/APICheckRole";
import { useTicket } from "Hooks/Endpoints";
import { filter } from "lodash";
import APIAccountProvider from "Providers/APIAccountProvider";
import React, { useEffect, useState } from "react";
import { Button, Container, Dropdown, Menu } from "semantic-ui-react";
import ExportNumberForm from "./ExportNumberForm";
import GlobalNumberForm from "./GlobalNumberForm";
import MigrateNumberForm from "./MigrateNumberForm";
import TicketList from "./TicketList";
import TicketPagination from "./TicketPagination";
import TicketSubmissionList from "./TicketSubmissionList";

const TicketListHolder = () => {
  const { hasRole, roles } = useCheckRole();

  const {
    search,
    select,
    tickets,
    filterRequest,
    doFilter,
    filterSubmissionRequest,
    doFilterSubmissions,
    submissions,
    clearFilter,
  } = useTicket();
  const [activeView, setActiveView] = useState<string>("Tickets");
  const [oldFilter, setOldFilter] = useState<TicketFilterRequest>();
  const roleViewSubmission = hasRole(roles.ticket.views.submissions);
  const admin = hasRole(roles.admin);

  useEffect(() => {
    if (activeView === "Submissions") {
      setOldFilter({ ...filterRequest });
      const f = {
        page: 0,
        max: 500,
        sortField: "name",
        sortDirection: "ASC",
        status: ["open", "pending"],
        type: ["port-request"],
      };
      doFilter(f);
    }
    if (activeView === "Tickets" && oldFilter) {
      doFilter({ ...filterRequest });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  const onBack = () => {
    setActiveView("Tickets");
  };

  return (
    <Container fluid className='listContainer'>
      {activeView === "Tickets" && (
        <Menu compact stackable size='mini'>
          <Menu.Item>
            <Dropdown
              text={activeView}
              icon='filter'
              labeled
              button
              className='icon'
            >
              <Dropdown.Menu>
                <Dropdown.Menu scrolling>
                  <Dropdown.Item onClick={() => setActiveView("Tickets")}>
                    Tickets
                  </Dropdown.Item>
                  {roleViewSubmission && (
                    <Dropdown.Item onClick={() => setActiveView("Submissions")}>
                      Port Submissions
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown.Menu>
            </Dropdown>
          </Menu.Item>
          <Menu.Item>
            <MenuSearch
              onSearch={search}
              placeholder='Search Tickets'
              value={filterRequest.text}
            />
          </Menu.Item>
          <Menu.Item header>Type</Menu.Item>
          <Menu.Item>
            <TicketTypeSelect
              style={{ width: "500px" }}
              value={filterRequest.type}
              name='ticket-type'
              onChange={(val: string) => {
                doFilter({
                  ...filterRequest,
                  type: val,
                  page: 0,
                });
              }}
            />
          </Menu.Item>
          <Menu.Item header>Status</Menu.Item>
          <Menu.Item>
            <TicketStatusSelect
              style={{ width: "260px" }}
              value={filterRequest.status}
              name='ticket-status'
              onChange={(val: string) => {
                doFilter({
                  ...filterRequest,
                  status: val,
                  page: 0,
                });
              }}
            />
          </Menu.Item>
          <Menu.Item header>Account</Menu.Item>
          <Menu.Item>
            <APIAccountProvider>
              <AccountSelectWithContext
                style={{ width: "200px" }}
                value={filterRequest.customerId}
                onChange={(val: DBReference) => {
                  doFilter({
                    ...filterRequest,
                    customerId: val ? val.id : undefined,
                    page: 0,
                  });
                }}
              />
            </APIAccountProvider>
          </Menu.Item>
          <Menu.Item>
            <Button primary onClick={() => clearFilter()}>
              Reset Filter
            </Button>
          </Menu.Item>
          {admin && (
            <Menu.Menu position='right'>
              <Dropdown item text='Create New Ticket'>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setActiveView("exportRequest")}>
                    Number Export
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => setActiveView("globalNumberRequest")}
                  >
                    Global Numbers
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => setActiveView("migrationRequest")}
                  >
                    Migration Request
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Menu.Menu>
          )}
        </Menu>
      )}
      {activeView === "Submissions" && (
        <Menu stackable size='mini'>
          <Menu.Item>
            <Dropdown
              text={activeView}
              icon='filter'
              labeled
              button
              className='icon'
            >
              <Dropdown.Menu>
                <Dropdown.Menu scrolling>
                  <Dropdown.Item onClick={() => setActiveView("Tickets")}>
                    Tickets
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setActiveView("Submissions")}>
                    Port Submissions
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Menu>
            </Dropdown>
          </Menu.Item>

          <Menu.Item header>Status</Menu.Item>
          <Menu.Item>
            <PortStatusSubmissionStatusSelect
              style={{ width: "790px" }}
              value={filterSubmissionRequest.status}
              name='ticket-status'
              onChange={(val: string) => {
                doFilterSubmissions({
                  ...filterSubmissionRequest,
                  status: val,
                });
              }}
            />
          </Menu.Item>

          <Menu.Item header>Account</Menu.Item>
          <Menu.Item>
            <APIAccountProvider>
              <AccountSelectWithContext
                style={{ width: "200px" }}
                value={filterSubmissionRequest.customerId}
                onChange={(val: DBReference) => {
                  doFilterSubmissions({
                    ...filterSubmissionRequest,
                    customerId: val ? val.id : undefined,
                  });
                }}
              />
            </APIAccountProvider>
          </Menu.Item>
        </Menu>
      )}
      {activeView === "Submissions" && (
        <TicketSubmissionList submissions={submissions} select={select} />
      )}
      {activeView === "Tickets" && (
        <TicketList tickets={tickets} select={select} />
      )}
      {activeView === "Tickets" && (
        <TicketPagination
          tickets={tickets}
          filter={filterRequest}
          setFilter={doFilter}
        />
      )}
      {activeView === "exportRequest" && (
        <APIAccountProvider>
          <ExportNumberForm onBack={onBack} />
        </APIAccountProvider>
      )}
      {activeView === "globalNumberRequest" && (
        <GlobalNumberForm onBack={onBack} />
      )}

      {activeView === "migrationRequest" && (
        <APIAccountProvider>
          <MigrateNumberForm onBack={onBack} />
        </APIAccountProvider>
      )}
    </Container>
  );
};

export default TicketListHolder;
